<template>
  <div
    v-if="isComponentVisible"
  >
    <v-card-title
      v-if="isVisible"
      :style="config.titleCardStyle || {}"
    >
      <v-row
        class="main-row"
      >
        <v-col
          v-if="canShowBackAndTitle"
          class="remove-padding title-back-button"
        >
          <universal-button
            v-if="canShowBackButton"
            type="back"
            class="mr-2"
            @click="onBack"
          />

          <div
            v-if="!isMobile"
            class="actions-title"
            :title="titleText"
          >
            {{ titleText }}
          </div>
        </v-col>

        <v-spacer
          v-if="showButtonsRight && (canShowAdditionalButtons || !isMobile)"
        />

        <v-col
          v-if="!isMobile || canShowAdditionalButtons"
          class="d-flex justify-end align-center remove-padding"
          cols="auto"
        >
          <!--          Additional buttons, placed before edit buttons (if exists)-->
          <template
            v-if="canShowAdditionalButtons"
          >
            <template
              v-for="(button, buttonIndex) in config.additional_buttons"
            >
              <div
                v-if="canShowAdditionalButton(button)"
                :key="buttonIndex"
                :style="{ cursor: permissions && !permissions.canEdit ? 'not-allowed' : 'default', position: 'relative'}"
              >
                <!--                button with dropdown menu-->
                <template
                  v-if="button.type === 'dropdown' && button.component"
                >
                  <v-menu
                    offset-y
                    :content-class="button.menuContentClass || ''"
                    :close-on-content-click="!!button.closeOnContentClick"
                  >
                    <template
                      #activator="{ on }"
                    >
                      <div
                        v-on="on"
                      >
                        <universal-button
                          :type="button && button.buttonType ? button.buttonType : 'base'"
                          :button-type="button.type"
                          :prepend-icon="getPrependIcon(button)"
                          :append-icon="getAppendIcon(button)"
                          :show-loader="button.showLoader !== undefined ? button.showLoader : false"
                          :label="button.label || ''"
                          :disabled="(permissions && !permissions.canEdit) || (button.disabled !== undefined ? button.disabled : false)"
                          :color="button.color || null"
                          :class="button.class || ''"
                          :style="button.style && isObject(button.style) ? button.style : {}"
                          class="mr-2"
                        />
                      </div>
                    </template>
                    <component
                      :is="button.component"
                      :edit="edit"
                      :create-mode="createMode"
                      :form-data="formData"
                      :config="button"
                    />
                  </v-menu>
                </template>
                <!--                custom button component-->
                <template
                  v-else-if="button.component && (button.visible === undefined || button.visible === true)"
                >
                  <component
                    :is="button.component"
                    :edit="edit"
                    :create-mode="createMode"
                    :form-data="formData"
                    :config="button"
                    class="mr-3"
                  />
                </template>
                <!--                base button-->
                <template
                  v-else-if="button && (button.visible === undefined || button.visible === true)"
                >
                  <universal-button
                    :type="button && button.type ? button.type : 'base'"
                    :button-type="button.type"
                    :prepend-icon="getPrependIcon(button)"
                    :prepend-icon-style="button.prependIconStyle"
                    :append-icon="getAppendIcon(button)"
                    :show-loader="button.showLoader !== undefined ? button.showLoader : false"
                    :loader-config="button.loaderConfig || {}"
                    :label="button.label || ''"
                    :disabled="(permissions && !permissions.canEdit) || (button.disabled !== undefined ? button.disabled : false)"
                    :color="button.color || null"
                    :class="button.class || ''"
                    :style="button.style && isObject(button.style) ? button.style : {}"
                    class="mr-2"
                    @click="onAdditionalButtonClick(button)"
                  />
                </template>
              </div>
            </template>
          </template>

          <template
            v-if="!isMobile"
          >
            <!--          Edit buttons, controls visibility via config parameter 'showEditButtons'-->
            <!--          Cant be configurable for now, since we want consistency, to these edit buttons be the same everywhere in app-->
            <div
              v-if="canShowEditButtons"
              :style="{ cursor: permissions && !permissions.canEdit ? 'not-allowed' : 'default' }"
            >
              <!--            Edit button-->
              <universal-button
                v-if="!isEditing && !createMode && !config.createMode && (config.showEditBtn === undefined || config.showEditBtn === true)"
                type="edit"
                :disabled="(permissions && !permissions.canEdit) || (config.disableEdit === true)"
                @click="onEditClick"
              />
            </div>

            <!--          Cancel tab edit button-->
            <universal-button
              v-if="canShowEditButtons && (isEditing || createMode || config.createMode)"
              class="mr-2"
              type="cancel"
              :disabled="config.disableCancel === true"
              @click="onCancelTabSave"
            />

            <!--          Save tab data button-->
            <universal-button
              v-if="canShowEditButtons && (isEditing || createMode || config.createMode) && (config.showSaveBtn === undefined || config.showSaveBtn === true)"
              :disabled="config.disableSave === true"
              :button-type="saveButtonType"
              @click="onSave"
            />
          </template>
        </v-col>
      </v-row>
    </v-card-title>

    <div
      v-if="showMobileFooterActions"
      ref="mobileActionsBar"
      class="mobile-actions-bar"
    >
      <div
        v-if="showMobileEditButton"
        class="mobile-actions-bar__item mobile-actions-bar__item--single"
      >
        <universal-button
          type="edit"
          style="width: 100%;"
          :disabled="(permissions && !permissions.canEdit) || (config.disableEdit === true)"
          @click="onEditClick"
        />
      </div>

      <template
        v-else
      >
        <div
          v-if="showMobileCancelButton"
          class="mobile-actions-bar__item"
        >
          <universal-button
            type="cancel"
            style="width: 100%;"
            :disabled="config.disableCancel === true"
            @click="onCancelTabSave"
          />
        </div>

        <div
          v-if="showMobileSaveButton"
          class="mobile-actions-bar__item"
        >
          <universal-button
            style="width: 100%;"
            :disabled="config.disableSave === true"
            :button-type="saveButtonType"
            @click="onSave"
          />
        </div>
      </template>
    </div>

    <information-dialog
      v-if="showChangesNotSavedDialog"
      :open-dialog="showChangesNotSavedDialog"
      :title="$t('base.warning')"
      :dialog-message="$t('fleet/vehicle.data_not_saved_message')"
      :positive-button-text="$t('base.confirm')"
      :negative-button-text="$t('base.cancel')"
      :confirm-callback-function="redirect"
      @close="showChangesNotSavedDialog = false"
    />
  </div>
</template>

<script>
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import InformationDialog from '@/global/components/dialogs/InformationDialog.vue'
import { setEditExternal } from '../common/external-to-content-shared-events'
import { setEdit } from '../common/content-shared-events'
import {
  getBooleanFromValue,
  isBoolean,
  isEmpty,
  isFunction,
  isObject,
  isString
} from '@/global/utils/utils'
import helperMixin from '@/global/mixins/helperMixin'

const DEFAULT_SAVE_BUTTON_TYPE = 'submit'
const VALID_BUTTONS_TYPES = ['submit', 'reset', 'button']
const DEFAULT_IS_VISIBLE = true
export default {
  name: 'TabActionButtons',

  components: {
    InformationDialog,
    UniversalButton
  },

  mixins: [
    helperMixin
  ],

  props: {
    edit: {
      type: Boolean,
      default: false
    },
    permissions: {
      type: Object,
      default: null
    },
    createMode: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: [Object, Array],
      default: () => ({})
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    tabsUpdate: {
      type: Object,
      default: () => ({})
    },
    external: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    editableTabs: {
      type: Array,
      default: () => ([])
    },
    fetchTabData: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      showChangesNotSavedDialog: false,
      isEditing: false
    }
  },

  computed: {
    computedConfig () {
      return this.config
    },

    isComponentVisible () {
      const { visible } = this.computedConfig || {}
      let resultVisibility = visible
      if (isFunction(visible)) {
        resultVisibility = visible()
      }

      return isBoolean(resultVisibility) ? resultVisibility : DEFAULT_IS_VISIBLE
    },

    showButtonsRight () {
      const { showButtonsRight = true } = this.computedConfig || {}
      return isBoolean(showButtonsRight) ? showButtonsRight : false
    },

    isVisible () {
      const { visible = true } = this.config || {}
      return getBooleanFromValue(visible)
    },

    canShowBackButton () {
      const { showBackButton = true } = this.config
      const { uiViewName, onGoBack } = showBackButton || {}
      return getBooleanFromValue(showBackButton) && (isString(uiViewName) || isFunction(onGoBack))
    },

    canShowBackAndTitle () {
      const { title } = this.config
      return title || this.canShowBackButton
    },

    canShowEditButtons () {
      const { showEditButtons = true } = this.config || {}
      return getBooleanFromValue(showEditButtons)
    },

    saveButtonType () {
      const { saveButtonType } = this.config || {}
      return VALID_BUTTONS_TYPES.includes(saveButtonType) ? saveButtonType : DEFAULT_SAVE_BUTTON_TYPE
    },

    canShowAdditionalButtons () {
      const { additional_buttons: additionalButtons, forceAdditionalButtonsDisplay } = this.config || {}
      return getBooleanFromValue(additionalButtons?.length) && (forceAdditionalButtonsDisplay || (!this.isEditing && !this.createMode))
    },

    titleText () {
      const { title } = this.config || {}
      return title || ''
    },

    showMobileEditButton () {
      return this.isMobile && this.canShowEditButtons && !this.isEditing && !this.createMode && !this.config.createMode && (this.config.showEditBtn === undefined || this.config.showEditBtn === true)
    },

    showMobileCancelButton () {
      return this.isMobile && this.canShowEditButtons && (this.isEditing || this.createMode || this.config.createMode)
    },

    showMobileSaveButton () {
      return this.isMobile && this.canShowEditButtons && (this.isEditing || this.createMode || this.config.createMode) && (this.config.showSaveBtn === undefined || this.config.showSaveBtn === true)
    },

    showMobileFooterActions () {
      return this.showMobileEditButton || this.showMobileCancelButton || this.showMobileSaveButton
    }
  },

  watch: {
    edit: {
      immediate: true,
      handler (val) {
        this.isEditing = !!val
      }
    },
    'config.edit': {
      immediate: true,
      deep: true,
      handler (val) {
        if (val !== undefined) {
          this.isEditing = !!val
        }
      }
    },
    tabsUpdate: {
      immediate: true,
      deep: true,
      handler (event) {
        const { edit } = event || {}
        if (edit && isObject(edit) && !isEmpty(edit)) {
          let anyTabInEditMode = false
          if (isObject(edit) && !isEmpty(edit) && this.editableTabs?.length) {
            this.editableTabs.forEach(key => {
              if (edit[key] === true) {
                anyTabInEditMode = true
              }
            })

            this.isEditing = anyTabInEditMode
          }
        }
      }
    },
    isEditing: {
      immediate: true,
      handler (val) {
        const { editUpdate } = this.config || {}
        if (typeof editUpdate === 'function') {
          editUpdate({
            value: val
          })
        }
      }
    }
  },

  methods: {
    isObject (val) {
      return isObject(val)
    },

    canShowAdditionalButton (button) {
      const { visible } = button || {}
      let canShow = visible

      if (visible === undefined) {
        return true
      }

      if (typeof visible === 'function') {
        canShow = visible({
          formData: this.formData,
          permissions: this.permissions
        })
      }

      return !!canShow
    },

    onAdditionalButtonClick (button) {
      const { click } = button

      if (click && typeof click === 'function') {
        click({
          instance: this,
          formData: this.formData,
          updateData: this.updateData,
          fetchTabData: this.fetchTabData,
          permissions: this.permissions
        })
      }
    },

    onCancelTabSave () {
      const { onCancelTabSave } = this.config || {}

      if (typeof onCancelTabSave === 'function') {
        onCancelTabSave({
          formData: this.formData,
          updateData: (val) => this.updateData(val),
          cancelTabSave: () => {
            this.cancelTabSave()
            if (this.editableTabs?.length && this.external) {
              setEditExternal(this, this.editableTabs, false)
              this.isEditing = !this.isEditing
            }
          }
        })
        return
      }

      this.$emit('cancel-tab-save')
      if (this.editableTabs?.length && this.external) {
        setEditExternal(this, this.editableTabs, false)
        this.isEditing = !this.isEditing
      }
    },

    cancelTabSave () {
      this.$emit('cancel-tab-save')
    },

    onBack () {
      if (this.createMode || !this.isEditing) {
        const { onGoBack, uiViewName, query } = this.config?.showBackButton || {}

        if (typeof onGoBack === 'function') {
          onGoBack()
        }
        else if (uiViewName && typeof uiViewName === 'string') {
          let queryParam = {}
          if (typeof query === 'function') {
            queryParam = query()
          }

          this.$router.replace({
            name: uiViewName,
            query: queryParam
          })
        }
      }
      else {
        this.showChangesNotSavedDialog = true
      }
    },

    redirect () {
      const { uiViewName, onGoBack } = this.config?.showBackButton || ''

      if (uiViewName && typeof uiViewName === 'string') {
        this.$router.replace({ name: uiViewName })
      }
      else if (typeof onGoBack === 'function') {
        onGoBack()
      }
      this.showChangesNotSavedDialog = false
    },

    getPrependIcon (button) {
      const { prependIcon } = button || {}
      let returnVal = prependIcon

      if (typeof prependIcon === 'function') {
        returnVal = prependIcon({
          data: this.formData
        })
      }

      return returnVal && typeof returnVal === 'string' ? returnVal : ''
    },

    getAppendIcon (button) {
      const { appendIcon } = button || {}
      let returnVal = appendIcon

      if (typeof appendIcon === 'function') {
        returnVal = appendIcon({
          data: this.formData
        })
      }

      return returnVal && typeof returnVal === 'string' ? returnVal : ''
    },

    onEditClick () {
      const { onEditClick } = this.config || {}
      if (this.external) {
        this.isEditing = true
      }
      setEdit(this, true)
      // Set edit from external component
      if (this.editableTabs?.length && this.external) {
        setEditExternal(this, this.editableTabs, true)
      }

      if (typeof onEditClick === 'function') {
        onEditClick()
      }
    },

    onSave () {
      const { onSaveClick } = this.config || {}
      if (typeof onSaveClick === 'function') {
        onSaveClick({
          saveTab: () => this.$emit('save-tab'),
          validationErrors: this.validationErrors
        })
        return
      }

      this.$emit('save-tab')
    }
  }
}
</script>

<style scoped>
.title-back-button {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.actions-title {
  font-family: 'Satoshi', sans-serif !important;
  font-size: 17px !important;
  font-weight: 700;
  color: #111827;
  line-height: 24px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.main-row {
  margin: 0;
}

.remove-padding {
  padding: 0 !important;
}

.mobile-actions-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 12;
  display: flex;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -6px 20px rgba(17, 24, 39, 0.08);
}

.mobile-actions-bar__item {
  flex: 1 1 0;
  min-width: 0;
}

.mobile-actions-bar__item--single {
  flex: 1 1 100%;
}
</style>
